// A row of the deck's own colours, plus an "inherit" chip (TODO §26/§6.3, §15/§7.5).
//
// **Names, never hexes.** The value shown is resolved for the swatch, but what `onPick` hands
// back is the colour's NAME — so a sheet swap recolours everything that referenced it. That
// indirection is the whole of §6.2.2, and offering a raw hex field instead is the single
// easiest way to break it, which is why there is no hex field anywhere in this component.
//
// §26 wrote this inside `GanttPanel`; §15's Chart style panel needs exactly the same control
// for a series colour and for each palette slot, so it moved here rather than being copied.
// A copy is a second place for the two pickers to drift apart.

import { cn } from "@/lib/utils";

export function SwatchRow({
  swatches,
  value,
  onPick,
  inheritLabel,
  inheritColor,
}: {
  swatches: { name: string; value: string }[];
  // The currently picked NAME, or undefined for "inherit".
  value: string | undefined;
  onPick: (name: string | undefined) => void;
  // What "no explicit choice" means here — "chart default", "deck palette", "none".
  inheritLabel: string;
  // The colour that choice actually resolves to, shown as the chip's left edge so "inherit"
  // is not an invisible option.
  inheritColor: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={() => onPick(undefined)}
        title={inheritLabel}
        aria-label={inheritLabel}
        aria-pressed={!value}
        className={cn(
          "h-6 rounded border px-2 text-[11px] text-slate-600",
          !value
            ? "border-slate-900 ring-2 ring-slate-900/20"
            : "border-slate-200",
        )}
        style={{ borderLeftColor: inheritColor, borderLeftWidth: 6 }}
      >
        {inheritLabel}
      </button>
      {swatches.map((swatch) => (
        <button
          key={swatch.name}
          type="button"
          onClick={() => onPick(swatch.name)}
          title={swatch.name}
          aria-label={swatch.name}
          aria-pressed={value === swatch.name}
          className={cn(
            "h-6 w-6 rounded border",
            value === swatch.name
              ? "border-slate-900 ring-2 ring-slate-900/20"
              : "border-slate-300",
          )}
          style={{ backgroundColor: swatch.value }}
        />
      ))}
    </div>
  );
}
